import { withAlpha } from "../../common/color.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import { Control } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import type { Rect, Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { Theme } from "../themes/Theme.js";

export interface DatePickerOptions extends ContainerOptions {
  /** The date shown. Null leaves the field empty. */
  value?: Date | null;
  /** Earliest and latest date that can be picked. */
  minimum?: Date | null;
  maximum?: Date | null;
  /** 0 for Sunday, 1 for Monday. */
  firstDayOfWeek?: number;
  /** How the date is written in the field. */
  format?: (date: Date) => string;
  onChange?: (value: Date | null) => void;
}

const CELL = 30;
const HEADER_HEIGHT = 30;
const WEEKDAY_HEIGHT = 20;
const CALENDAR_PADDING = 6;
const COLUMNS = 7;
const ROWS = 6;

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * A date field with a month calendar behind it.
 *
 * Dates are compared by day, never by timestamp: two `Date`s for the same
 * afternoon are different numbers, and a picker that compared them directly
 * would fail to highlight the day the user is looking at. `sameDay` and
 * `startOfDay` are exported because anything holding a picked date will need
 * the same rule.
 */
export class DatePicker extends Container {
  #value: Date | null;
  #calendar: MonthCalendar | null = null;

  minimum: Date | null;
  maximum: Date | null;
  firstDayOfWeek: number;
  format: (date: Date) => string;

  constructor(options: DatePickerOptions = {}) {
    super({ width: 160, height: 28, tabStop: true, cursor: "pointer", ...options });
    // Through the same normalization the setter uses: a date carrying a time
    // would never compare equal to a day picked off the calendar.
    this.#value = options.value ? startOfDay(options.value) : null;
    this.minimum = options.minimum ?? null;
    this.maximum = options.maximum ?? null;
    this.firstDayOfWeek = options.firstDayOfWeek ?? 0;
    this.format = options.format ?? ((d) => d.toLocaleDateString());
    if (this.#value) this.#value = this.#clampToRange(this.#value);
    if (options.onChange) this.on("change", () => options.onChange!(this.#value));

    this.on("click", () => this.toggleDropDown());
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  get value(): Date | null {
    return this.#value;
  }
  set value(v: Date | null) {
    const next = v ? startOfDay(v) : null;
    if (next && this.#value && sameDay(next, this.#value)) return;
    if (!next && !this.#value) return;
    this.#value = next && this.isInRange(next) ? next : next ? this.#clampToRange(next) : null;
    this.emit("change");
    this.invalidate();
  }

  get droppedDown(): boolean {
    return this.#calendar !== null;
  }

  /** True when `date` is between `minimum` and `maximum`, inclusive. */
  isInRange(date: Date): boolean {
    const day = startOfDay(date).getTime();
    if (this.minimum && day < startOfDay(this.minimum).getTime()) return false;
    if (this.maximum && day > startOfDay(this.maximum).getTime()) return false;
    return true;
  }

  #clampToRange(date: Date): Date {
    if (this.minimum && date < startOfDay(this.minimum)) return startOfDay(this.minimum);
    if (this.maximum && date > startOfDay(this.maximum)) return startOfDay(this.maximum);
    return date;
  }

  toggleDropDown(): void {
    if (this.droppedDown) this.closeDropDown();
    else this.openDropDown();
  }

  openDropDown(): void {
    const desktop = this.desktop;
    if (!desktop || this.droppedDown || !this.effectivelyEnabled) return;

    const width = COLUMNS * CELL + CALENDAR_PADDING * 2;
    const height = HEADER_HEIGHT + WEEKDAY_HEIGHT + ROWS * CELL + CALENDAR_PADDING * 2;
    const anchor = this.screenBounds;

    const calendar = new MonthCalendar(this, {
      x: Math.min(anchor.x, Math.max(0, desktop.width - width)),
      y: anchor.y + anchor.height,
      width,
      height,
    });
    // Flip above when there is no room below, the way a combo does.
    if (calendar.y + height > desktop.height) calendar.y = Math.max(0, anchor.y - height);

    calendar.on("change", () => {
      const picked = calendar.picked;
      this.closeDropDown();
      if (picked) this.value = picked;
      this.focus();
    });
    calendar.on("blur", () => this.closeDropDown());
    calendar.on("keydown", (e) => {
      if (e.key !== "Escape") return;
      this.closeDropDown();
      this.focus();
      e.handled = true;
    });

    this.#calendar = calendar;
    desktop.showPopup(calendar);
    calendar.focus();
    this.invalidate();
  }

  closeDropDown(): void {
    const calendar = this.#calendar;
    if (!calendar) return;
    const desktop = this.desktop;
    this.#calendar = null;
    if (desktop?.popup === calendar) desktop.closePopup();
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (e.key === "Enter" || e.key === " " || (e.key === "ArrowDown" && e.altKey) || e.key === "F4") {
      this.toggleDropDown();
      e.handled = true;
      return;
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      this.value = null;
      e.handled = true;
    }
  }


  /**
   * One theme control-height tall; the width stays whatever it was given.
   *
   * A field has no content width worth measuring — it is as wide as the form wants it, not as
   * wide as the text someone happens to have typed — so the only interesting axis is the one the
   * theme decides.
   */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    return { width: this.width, height: desktop.theme.metrics.controlHeight };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const r = this.localRect;
    theme.drawTextBox(rc, r, { ...this.visualState(), pressed: this.droppedDown });

    const pad = theme.metrics.textBoxPadding;
    const glyph = 12;
    rc.drawText(
      this.#value ? this.format(this.#value) : "",
      pad.left,
      r.height / 2,
      theme.metrics.font,
      this.effectivelyEnabled ? p.fieldText : p.textDisabled,
      { baseline: "middle", maxWidth: Math.max(0, r.width - pad.left - glyph - pad.right * 2) },
    );
    paintGlyph("chevron-down", {
      rc,
      box: { x: r.width - pad.right - glyph, y: (r.height - glyph) / 2, width: glyph, height: glyph },
      ink: p.textSubtle,
      weight: 1.5,
    });
  }
}

/** The month grid. Made by `DatePicker`; not built directly. */
class MonthCalendar extends Control {
  #owner: DatePicker;
  /** The month on show. */
  #month: Date;
  /** The day the keyboard is on. */
  #cursor: Date;
  /** Set when a day is taken; the owner reads it on `change`. */
  picked: Date | null = null;
  #hovered = -1;

  constructor(owner: DatePicker, bounds: Rect) {
    super({ ...bounds, tabStop: true });
    this.#owner = owner;
    const start = owner.value ?? new Date();
    this.#month = new Date(start.getFullYear(), start.getMonth(), 1);
    this.#cursor = startOfDay(start);

    this.on("pointermove", (e) => this.#hover(e));
    this.on("click", (e) => this.#onClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  /** The first cell's date — may be in the previous month. */
  get gridStart(): Date {
    const first = new Date(this.#month.getFullYear(), this.#month.getMonth(), 1);
    const offset = (first.getDay() - this.#owner.firstDayOfWeek + 7) % 7;
    return new Date(first.getFullYear(), first.getMonth(), 1 - offset);
  }

  dateAtCell(index: number): Date {
    const start = this.gridStart;
    return new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
  }

  /** The cell containing a local point, or -1. */
  indexAt(x: number, y: number): number {
    const top = HEADER_HEIGHT + WEEKDAY_HEIGHT + CALENDAR_PADDING;
    const column = Math.floor((x - CALENDAR_PADDING) / CELL);
    const row = Math.floor((y - top) / CELL);
    if (column < 0 || column >= COLUMNS || row < 0 || row >= ROWS) return -1;
    return row * COLUMNS + column;
  }

  shiftMonth(delta: number): void {
    this.#month = new Date(this.#month.getFullYear(), this.#month.getMonth() + delta, 1);
    this.invalidate();
  }

  #take(date: Date): void {
    if (!this.#owner.isInRange(date)) return;
    this.picked = date;
    this.emit("change");
  }

  #hover(e: MouseEventArgs): void {
    const index = this.indexAt(e.x, e.y);
    if (index === this.#hovered) return;
    this.#hovered = index;
    this.invalidate();
  }

  #onClick(e: MouseEventArgs): void {
    if (e.y < HEADER_HEIGHT + CALENDAR_PADDING) {
      // The header: a chevron each side of the month's name.
      if (e.x < CALENDAR_PADDING + CELL) this.shiftMonth(-1);
      else if (e.x > this.width - CALENDAR_PADDING - CELL) this.shiftMonth(1);
      return;
    }
    const index = this.indexAt(e.x, e.y);
    if (index >= 0) this.#take(this.dateAtCell(index));
  }

  #onKeyDown(e: KeyEventArgs): void {
    const move = (days: number): void => {
      this.#cursor = new Date(
        this.#cursor.getFullYear(),
        this.#cursor.getMonth(),
        this.#cursor.getDate() + days,
      );
      // Following the cursor out of the month turns the page, so arrowing to
      // the 1st of next month simply works.
      this.#month = new Date(this.#cursor.getFullYear(), this.#cursor.getMonth(), 1);
      this.invalidate();
      e.handled = true;
    };
    switch (e.key) {
      case "ArrowLeft":
        move(-1);
        break;
      case "ArrowRight":
        move(1);
        break;
      case "ArrowUp":
        move(-7);
        break;
      case "ArrowDown":
        move(7);
        break;
      case "PageUp":
        this.shiftMonth(-1);
        e.handled = true;
        break;
      case "PageDown":
        this.shiftMonth(1);
        e.handled = true;
        break;
      case "Enter":
      case " ":
        this.#take(this.#cursor);
        e.handled = true;
        break;
      default:
        break;
    }
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const font = theme.metrics.font;
    rc.drawRect(this.localRect, {
      fill: p.windowBg,
      radius: theme.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });

    // Header: chevrons and the month.
    const glyph = 10;
    const headerMiddle = CALENDAR_PADDING + HEADER_HEIGHT / 2;
    paintGlyph("chevron-left", {
      rc,
      box: { x: CALENDAR_PADDING + 6, y: headerMiddle - glyph / 2, width: glyph, height: glyph },
      ink: p.textSubtle,
      weight: 1.5,
    });
    paintGlyph("chevron-right", {
      rc,
      box: {
        x: this.width - CALENDAR_PADDING - 6 - glyph,
        y: headerMiddle - glyph / 2,
        width: glyph,
        height: glyph,
      },
      ink: p.textSubtle,
      weight: 1.5,
    });
    const title = `${MONTHS[this.#month.getMonth()]} ${this.#month.getFullYear()}`;
    const titleWidth = rc.measureText(title, theme.metrics.captionFont).width;
    rc.drawText(title, (this.width - titleWidth) / 2, headerMiddle, theme.metrics.captionFont, p.text, {
      baseline: "middle",
    });

    // Weekday initials.
    const weekdayY = CALENDAR_PADDING + HEADER_HEIGHT;
    for (let i = 0; i < COLUMNS; i++) {
      const label = WEEKDAYS[(i + this.#owner.firstDayOfWeek) % 7]!;
      const width = rc.measureText(label, font).width;
      rc.drawText(
        label,
        CALENDAR_PADDING + i * CELL + (CELL - width) / 2,
        weekdayY + WEEKDAY_HEIGHT / 2,
        font,
        p.textSubtle,
        { baseline: "middle" },
      );
    }

    const gridTop = weekdayY + WEEKDAY_HEIGHT;
    const today = startOfDay(new Date());
    for (let i = 0; i < COLUMNS * ROWS; i++) {
      const date = this.dateAtCell(i);
      const cell: Rect = {
        x: CALENDAR_PADDING + (i % COLUMNS) * CELL,
        y: gridTop + Math.floor(i / COLUMNS) * CELL,
        width: CELL,
        height: CELL,
      };
      const thisMonth = date.getMonth() === this.#month.getMonth();
      const allowed = this.#owner.isInRange(date);
      const selected = this.#owner.value !== null && sameDay(date, this.#owner.value);
      const onCursor = sameDay(date, this.#cursor);

      if (selected) rc.fillRect(cell, p.accent, 3);
      else if (i === this.#hovered && allowed) rc.fillRect(cell, p.controlBgHover, 3);
      // Today is ringed rather than filled, so it never competes with the
      // selection for what the eye reads first.
      if (sameDay(date, today) && !selected) rc.strokeRect(cell, withAlpha(p.accent, 0.6), 1, 3);
      if (onCursor && this.focused && !selected) theme.drawFocusRing(rc, cell);

      const label = String(date.getDate());
      const width = rc.measureText(label, font).width;
      const ink = !allowed
        ? p.textDisabled
        : selected
          ? p.accentText
          : thisMonth
            ? p.text
            : p.textSubtle;
      rc.drawText(
        label,
        cell.x + (CELL - width) / 2,
        cell.y + CELL / 2,
        font,
        ink,
        { baseline: "middle" },
      );
    }
  }
}

/** Midnight on the same day, so two dates can be compared by day. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** True when two dates fall on the same day, whatever time they carry. */
export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
